import { useEffect, useState } from "react";
import { QrCode, ScanLine } from "lucide-react";
import { api } from "../../api";
import { trackEvent } from "../../analytics";

export default function Coa({ onRequestQuote }) {
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!result) return;
    // load history for this grade
    api.listCoa(result.grade).then((d) => setHistory(d.batches || [])).catch(() => setHistory([]));
  }, [result]);

  function search(term) {
    const batch = (term ?? input).trim();
    if (!batch) return;
    api
      .getCoa(batch)
      .then((d) => {
        setResult(d);
        setNotFound(null);
        trackEvent("coa_lookup_searched", { batch, found: true });
      })
      .catch(() => {
        setResult(null);
        setNotFound(batch);
        setHistory([]);
        trackEvent("coa_lookup_searched", { batch, found: false });
      });
  }

  const verifyUrl = result
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/#coa/${result.batch_number}`
    : "";
  const qrSrc = result
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=6&data=${encodeURIComponent(verifyUrl)}`
    : "";

  return (
    <section className="panel" id="coa" data-testid="section-coa">
      <div className="wrap">
        <div className="panel-head">
          <div className="eyebrow">Tool · COA Verifier</div>
          <h2>Batch traceability</h2>
          <p>
            Enter a batch number to pull the Certificate of Analysis, get a scannable QR code for
            audit, and see the previous batches under the same grade. Try{" "}
            <span className="mono">HCL-2603-114</span> or <span className="mono">NAOH-2605-071</span>.
          </p>
        </div>
        <div className="coa-box">
          <input
            className="mono"
            type="text"
            placeholder="e.g. HCL-2603-114"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            data-testid="coa-input"
          />
          <button
            className="btn"
            style={{ background: "var(--ink)", color: "#fff" }}
            onClick={() => search()}
            data-testid="coa-search-btn"
          >
            <ScanLine size={14} /> Verify batch
          </button>
        </div>

        <div className="coa-result" data-testid="coa-result">
          {result && (
            <div className="coa-verified-grid">
              <div>
                <span className="coa-pass" data-testid="coa-result-pass">{result.result}</span>
                <h3 style={{ marginBottom: 12 }}>{result.batch_number}</h3>
                <table>
                  <tbody>
                    <tr><td>Grade</td><td>{result.grade}</td></tr>
                    <tr><td>Batch date</td><td>{result.date}</td></tr>
                    <tr><td>Assay</td><td>{result.assay}</td></tr>
                    <tr><td>Iron (Fe)</td><td>{result.fe}</td></tr>
                    <tr><td>Heavy metals</td><td>{result.heavy_metals}</td></tr>
                    <tr><td>Appearance</td><td>{result.appearance}</td></tr>
                  </tbody>
                </table>
                <div className="card-actions" style={{ marginTop: 12 }}>
                  <button className="print-btn" onClick={() => window.print()} data-testid="coa-print">Print COA</button>
                  <button className="btn btn-brand btn-sm" onClick={() => onRequestQuote?.("coa-lookup")} data-testid="coa-request-quote">
                    Request a quote for this grade
                  </button>
                </div>
              </div>
              <div className="coa-qr" data-testid="coa-qr">
                <img src={qrSrc} alt={`QR for ${result.batch_number}`} width={180} height={180} />
                <div className="mono coa-qr-label"><QrCode size={13} /> Scan to verify</div>
                <div className="coa-qr-url mono">{verifyUrl}</div>
              </div>
            </div>
          )}
          {notFound && (
            <p className="note" data-testid="coa-not-found">
              No batch found for <span className="mono">{notFound}</span>. Double-check the batch
              number on the tanker or container label, or contact QC.
            </p>
          )}
          {!result && !notFound && (
            <p className="note">
              No batch searched yet. Batch numbers follow{" "}
              <span className="mono">[GRADE]-[WEEK][YEAR]-[SEQ]</span>.
            </p>
          )}
        </div>

        {history.length > 1 && (
          <div className="coa-history" data-testid="coa-history">
            <div className="sub-panel-head">
              <div className="eyebrow">Batch history · {result?.grade}</div>
              <h3>Previous releases for this grade</h3>
            </div>
            <div className="coa-history-list">
              {history.map((b) => (
                <button
                  key={b.batch_number}
                  className={"coa-history-item " + (b.batch_number === result.batch_number ? "on" : "")}
                  onClick={() => search(b.batch_number)}
                  data-testid={`coa-history-${b.batch_number}`}
                >
                  <div>
                    <div className="mono">{b.batch_number}</div>
                    <div className="coa-history-date">{b.date}</div>
                  </div>
                  <div className="mono coa-history-assay">{b.assay}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
